/**
 * ErrorBoundary — top-level React error boundary for the admin panel. Wraps the
 * app (or a subtree) and, when a child throws during render/lifecycle, swaps in a
 * localized fallback screen instead of letting the whole page blank out. Must be a
 * class component because only class components can be error boundaries in React.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/** The subtree this boundary protects. */
interface Props {
  children: ReactNode;
}

/** Whether a child error has been caught, and the caught error itself. */
interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches JavaScript errors in child components and shows a fallback UI
 * instead of blanking the whole app.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  // React calls this when a descendant throws; flip into the error state to render the fallback.
  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  // Side-effect hook for the same error — log it to the console for debugging.
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  // "Try again" — clear the error state so the children get a fresh render attempt.
  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback onRetry={this.handleRetry} />
      );
    }
    return this.props.children;
  }
}

/**
 * Full-screen fallback shown after an error is caught. Offers two recovery paths:
 * "Try again" (re-render the subtree via `onRetry`) and "Go to dashboard" (hard
 * navigation to `/`). Kept as a separate function component so it can use the
 * `useTranslation` hook, which the class boundary cannot call directly.
 */
function ErrorFallback({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center' }} elevation={2}>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          {t('errors.somethingWentWrong')}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {t('errors.unexpectedError')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={onRetry}>
            {t('errors.tryAgain')}
          </Button>
          <Button variant="outlined" onClick={() => (window.location.href = '/')}>
            {t('errors.goToDashboard')}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
